import { useGetCmsPages } from '@agos/api-client/marketing'
import { Toaster } from '@agos/ui'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { useState } from 'react'
import { Route, Switch, useLocation } from 'wouter'

import { BranchGate } from './branch/BranchGate'
import { BrandingApplier } from './branding/BrandingApplier'
import { BottomNav } from './chrome/BottomNav'
import { Footer, Header } from './chrome/Chrome'
import { InstallPwaPrompt } from './chrome/InstallPwaPrompt'
import { composedBanners, composedRoutes, footerRoutes } from './module-registry'
import { NotFoundPage } from './NotFoundPage'
import { ServiceGate } from './ServiceGate'

/**
 * The customer app's shell.
 *
 * It composes and it gates; it knows no feature. The routes come from `module-registry`, which each
 * `modules/customer-*` package fills in through `module-contract` (§5, and §0A.2's rework of this app
 * after the first draft put ~8,800 lines of feature code inside the shell).
 *
 * **The branch gate wraps everything**, because every other screen assumes a resolved franchise. It is
 * the second half of the inline script in `index.html`: that script decides what it can before the
 * bundle exists, and this decides the rest once `central/branches` has answered.
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // A customer's menu does not change while they read it; a refetch mid-scroll moves the page under
      // their thumb. The manager app polls because a kitchen board must; this does not.
      refetchOnWindowFocus: false,
      staleTime: 60_000,
    },
  },
})

const ROUTES = composedRoutes()
const BANNERS = composedBanners()
const WITH_FOOTER = footerRoutes()

export function App() {
  const [client] = useState(() => queryClient)
  const [location] = useLocation()

  return (
    <QueryClientProvider client={client}>
      {/* Outside the gate: a visitor still choosing a branch is already looking at the page. */}
      <BrandingApplier />
      <BranchGate>
        {/* §9.187 - above the router, so maintenance closes the whole site rather than the home page
            alone, and a notice appears on every route. */}
        <ServiceGate>
        <Header />
        <Switch>
          {ROUTES.map((route) => {
            const Page = route.component
            return (
              <Route key={route.path} path={route.path}>
                <Page />
              </Route>
            )
          })}
          {/* Last, and unconditional: wouter's Switch takes the first match, so this is the catch-all. */}
          <Route>
            <NotFoundPage />
          </Route>
        </Switch>
        <Chrome showFooter={WITH_FOOTER.has(location)} />
        {/* The install prompt is the shell's own - §0A.2 puts PWA config here - and the rest belong to
            the module that owns the permission each asks for. All three render outside the router,
            where legacy's App.tsx mounts them, so they appear on every route rather than only on the
            home page (FB-C13). */}
        <InstallPwaPrompt />
        {BANNERS.map((banner) => {
          const Banner = banner.component
          return <Banner key={banner.name} />
        })}
        {/* The manager's toast host, reused rather than rebuilt - §7 keeps Radix in `packages/ui`, and
            a second implementation would be a second set of a11y bugs. */}
        <Toaster />
        </ServiceGate>
      </BranchGate>
    </QueryClientProvider>
  )
}

/**
 * The pages both the footer and the phone navigation list.
 *
 * One query for two consumers: legacy fetches `/cms-pages` separately in each, so a customer opening the
 * "Ďalšie" sheet pays for a list the footer already has.
 */
function Chrome({ showFooter }: { showFooter: boolean }) {
  const { data } = useGetCmsPages()
  const pages = (Array.isArray(data) ? data : []).filter(
    (row): row is { id: number; slug: string; title: string } =>
      typeof row === 'object' &&
      row !== null &&
      typeof (row as { slug?: unknown }).slug === 'string' &&
      typeof (row as { title?: unknown }).title === 'string' &&
      (row as { published?: unknown }).published !== false,
  )
  return (
    <>
      {showFooter && <Footer cmsPages={pages} />}
      <BottomNav cmsPages={pages} />
    </>
  )
}
